import React, { Fragment, useCallback, useEffect } from 'react';
import T from 'prop-types';
import { isEmpty, pick, some, isUndefined, uniq } from 'lodash-es';

import DataLayout from './DataLayout';
import ExportType from './ExportType';
import DataPoints from './DataPoints';
import GrowthRates from './GrowthRates';
import UserSegments from './UserSegments';
import GeoGranularity from './GeoGranularity';
import DateGranularity from './DateGranularity';
import RegionsCountries from './RegionsCountries';
import EntityGranularity from './EntityGranularity';
import SettingsBanner from './SettingsBanner';
import ExportDataUse from './ExportDataUse';
import SavedExportsDropdown from '../SavedExportsDropdown';
import { useRegions } from '../../../../hooks';

import { DATA_TYPE, RENDER_FIELDS, permissions } from '../../configs/constants';
import { csvOnlyDataPoints } from '../../configs/report';
import { assetMapping } from '../../../../utils/helpers';
import { differenceInDays } from '../../../../utils/date';

const ExportSettings = ({
  dataType,
  report,
  onReportChange,
  onPrefill,
}) => {
  const [regionsList] = useRegions(TINKERBELL_REGIONS);

  const isAdvancedDataType = dataType === DATA_TYPE.ADVANCED;
  const isMpiDataType = dataType === DATA_TYPE.MPI;

  const appsOnlySelected = report.entities?.length > 0
    && report.entities?.every(entity => assetMapping.apps.includes(entity.kind));

  const isBigReport = [
    report.countries_iso?.length > 5,
    report.countries_iso?.includes('WW'),
    (!!report.end_date && !!report.start_date
      && differenceInDays(report.start_date, report.end_date) > 730),
  ].some(Boolean);

  const csvOnlyDataPointSelected =
    isMpiDataType
    || (report.use_entity_relations === false && report.split_by_acquisition === false)
    || (report.use_entity_relations && report.split_by_acquisition)
    || report.growth_rates.length > 0
    || report.data_granularity.includes('_f')
    || report.data_granularity === 'weekly'
    || report.data_granularity === 'yearly'
    || report.data_points?.some(
      p => csvOnlyDataPoints(report.panel_version).includes(p),
    );

  //eslint-disable-next-line
  const { isCrossAppSelected, otherDataPointsSelected } = report
    ?.data_points?.reduce((acc, item) => {
      if (item.startsWith('cross_app')) {
        acc.isCrossAppSelected.push(item);
      } else {
        acc.otherDataPointsSelected.push(item);
      }
      return acc;
    }, { isCrossAppSelected: [], otherDataPointsSelected: [] });

  const showDisabledMask = some(pick(report, ['reportEntities', 'start_date', 'end_date']), isEmpty);

  const handleDataPointsChange = useCallback((value) => {
    onReportChange(prevState => ({
      ...prevState,
      data_points: value,
      ...(isCrossAppSelected
        ? { user_segments: uniq([...(prevState.user_segments || []), 'all_users']) }
        : {}),
    }));
  }, [isCrossAppSelected]);

  const handleUserSegmentsChange = useCallback((value) => {
    onReportChange(prevState => ({ ...prevState, user_segments: value }));
  }, []);

  const handleGrowthRatesChange = useCallback((value) => {
    onReportChange(prevState => ({ ...prevState, growth_rates: value }));
  }, []);

  const toggleWWAggregation = useCallback((value) => {
    onReportChange(prevState => ({
      ...prevState,
      include_ww: value,
    }));
  }, []);

  const handleCountriesChange = useCallback((value) => {
    onReportChange(prevState => ({
      ...prevState,
      countries_iso: value,
      include_ww: false,
      geo_granularity: value.includes('WW') ? 'custom' : 'country',
    }));
  }, []);

  const handleGeoGranularityChange = useCallback((value) => {
    onReportChange(prevState => ({ ...prevState, geo_granularity: value }));
  }, []);

  const handleExportDataUseChange = useCallback((value) => {
    onReportChange(prevState => ({ ...prevState, use_entity_relations: value }));
  }, []);

  const handleDateGranularityChange = useCallback((value) => {
    onReportChange(prevState => ({ ...prevState, data_granularity: value }));
  }, []);

  const handleEntityGranularityChange = useCallback((entityGranularity) => {
    onReportChange(prevState => ({ ...prevState, ...entityGranularity }));
  }, []);

  const handleDataLayoutChange = useCallback((value) => {
    onReportChange(prevState => ({ ...prevState, output_format: value }));
  }, []);

  const handleExportTypeChange = useCallback((value) => {
    onReportChange(prevState => ({ ...prevState, export_type: value }));
  }, []);

  const handleCrossAppsPairsChange = useCallback((value) => {
    onReportChange(prevState => ({ ...prevState, cross_apps_pairs: value }));
  }, []);

  // Effects
  useEffect(() => {
    if (isBigReport || csvOnlyDataPointSelected) {
      onReportChange(prevState => ({ ...prevState, export_type: 'csv' }));
    }
  }, [isBigReport, csvOnlyDataPointSelected]);

  if (!report?.entities) return null;

  return (
    <section className="upsert-export-form__export-settings">
      {showDisabledMask && <div className="disabled-block" />}
      <header className="export-settings__header align-items-center">
        <div className="export-settings__header-left">
          <h4 className="h4 export-settings__header-title">Export Settings</h4>
        </div>
        <div className="export-settings__header-right">
          <SavedExportsDropdown
            currentExport={report}
            onSelect={onPrefill}
          />
        </div>
      </header>
      <div className="export-settings__body">
        {isMpiDataType && (
          <Fragment>
            <SettingsBanner />
            <div className="export-settings__section-divider" />
          </Fragment>
        )}
        {RENDER_FIELDS[dataType].map((field) => {
          if (field === 'data_points') {
            return (
              <Fragment key={field}>
                <DataPoints
                  report={report}
                  isAdvancedDataType={isAdvancedDataType}
                  onChange={handleDataPointsChange}
                  onCrossAppsPairsChange={handleCrossAppsPairsChange}
                />
                <div className="export-settings__section-divider" />
              </Fragment>
            );
          }

          if (field === 'user_segments') {
            return (
              <Fragment key={field}>
                <UserSegments
                  active={report.user_segments}
                  panelVersion={report.panel_version}
                  activeDataPoints={report.data_points}
                  disabledNotCrossApp={isCrossAppSelected?.length > 0 && otherDataPointsSelected?.length === 0}
                  onChange={handleUserSegmentsChange}
                />
                <div className="export-settings__section-divider" />
              </Fragment>
            );
          }

          if (field === 'countries_iso') {
            return (
              <Fragment key={field}>
                <RegionsCountries
                  active={report.countries_iso}
                  regionsList={regionsList}
                  disabled={isAdvancedDataType}
                  hideWWAggregation={isAdvancedDataType}
                  enableWWAggregation={report.include_ww}
                  toggleWWAggregation={toggleWWAggregation}
                  onChange={handleCountriesChange}
                />
                <div className="export-settings__section-divider" />
              </Fragment>
            );
          }

          if (field === 'growth_rates' && permissions.allowAdvancedExportModal) {
            return (
              <Fragment key={field}>
                <GrowthRates
                  growth_rates={report.growth_rates}
                  widgetType="new"
                  onChange={handleGrowthRatesChange}
                />
                <div className="export-settings__section-divider" />
              </Fragment>
            );
          }

          if (field === 'growth_rates' && !permissions.allowAdvancedExportModal) {
            return (
              <Fragment key={field}>
                <GrowthRates
                  growth_rates={report.growth_rates}
                  widgetType="old"
                  onChange={handleGrowthRatesChange}
                />
                <div className="export-settings__section-divider" />
              </Fragment>
            );
          }

          if (permissions.allowAdvancedExportModal && field === 'use_entity_relations') {
            return (
              <Fragment key={field}>
                <ExportDataUse
                  active={report.use_entity_relations}
                  onChange={handleExportDataUseChange}
                />
                <div className="export-settings__section-divider" />
              </Fragment>
            );
          }

          if (field === 'geo_granularity') {
            return (
              <Fragment key={field}>
                <GeoGranularity
                  active={report.geo_granularity}
                  regionsList={regionsList}
                  countriesIso={report.countries_iso}
                  splitByCountryOnly={isAdvancedDataType}
                  onChange={handleGeoGranularityChange}
                />
                <div className="export-settings__section-divider" />
              </Fragment>
            );
          }

          if (field === 'data_granularity') {
            return (
              <Fragment key={field}>
                <DateGranularity
                  active={report.data_granularity}
                  dataType={dataType}
                  onChange={handleDateGranularityChange}
                />
                <div className="export-settings__section-divider" />
              </Fragment>
            );
          }
          if (field === 'data_schema') {
            return (
              <Fragment key={field}>
                <EntityGranularity
                  active={report.data_schema}
                  splitByAcquisition={report.split_by_acquisition}
                  appsOnlySelected={isAdvancedDataType ? false : appsOnlySelected}
                  unifiedStoreOnly={isAdvancedDataType}
                  splitByAcquisitionDisabled={isAdvancedDataType}
                  onChange={handleEntityGranularityChange}
                />
                <div className="export-settings__section-divider" />
              </Fragment>
            );
          }

          if (field === 'output_format') {
            return (
              <Fragment key={field}>
                <DataLayout
                  active={report.output_format}
                  onChange={handleDataLayoutChange}
                />
                <div className="export-settings__section-divider" />
              </Fragment>
            );
          }

          if (field === 'export_type') {
            return (
              <ExportType
                key={field}
                active={report.export_type}
                disableXLS={csvOnlyDataPointSelected}
                onChange={handleExportTypeChange}
              />
            );
          }

          return null;
        })}
      </div>
    </section>
  );
};

ExportSettings.propTypes = {
  dataType: T.string.isRequired,
  report: T.object.isRequired,
  onReportChange: T.func.isRequired,
  onPrefill: T.func.isRequired,
};

export default ExportSettings;
